import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Item, ItemSearchType } from 'app/modules/masters/item/model/item.model';
import { Doctor } from 'app/modules/masters/doctor/models/doctor.model';
import { Company } from 'app/modules/masters/companies/model/companies.model';
import { ItemService } from 'app/modules/masters/item/item.service';
import { CompaniesService } from 'app/modules/masters/companies/companies.service';
import { DoctorService } from 'app/modules/masters/doctor/doctor.service';
import { CustomersService } from 'app/modules/masters/customers/customers.service';
import { Customer } from 'app/modules/masters/customers/customer.model';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private _isExpanded$ = new BehaviorSubject<boolean>(false);

  private searchParamsSubject = new BehaviorSubject<{ term: string; type: string }>({
    term: '',
    type: 'All',
  });
  searchParams$ = this.searchParamsSubject.asObservable();

  private searchTermSource = new BehaviorSubject<string>('');
  searchTerm$ = this.searchTermSource.asObservable();

  private _dropdownOptions$ = new BehaviorSubject<string[]>([
    'Items',
    'Doctors',
    'Companies',
    'Customers',
  ]);
  dropdownOptions$ = this._dropdownOptions$.asObservable();

  // Default values
  private readonly DEFAULT_SKIP = 0;
  private readonly DEFAULT_TAKE = 10;
  private readonly LARGE_TAKE = 200000;

  constructor(
    private itemService: ItemService,
    private doctorService: DoctorService,
    private companiesService: CompaniesService,
    private customersService: CustomersService,
  ) {}

  setSearchTerm(term: string) {
    this.searchTermSource.next(term);
  }

  setSearchParams(params: { term: string; type: string }) {
    this.searchParamsSubject.next(params);
  }

  setExpanded(value: boolean) {
    this._isExpanded$.next(value);
    sessionStorage.setItem('searchExpanded', JSON.stringify(value));
  }

  getExpanded$() {
    const saved = JSON.parse(sessionStorage.getItem('searchExpanded') || 'false');
    this._isExpanded$.next(saved);
    return this._isExpanded$.asObservable();
  }

  get isExpanded(): boolean {
    return JSON.parse(sessionStorage.getItem('searchExpanded') || 'false');
  }

  // Items
  searchItems(
    term: string,
    type: ItemSearchType,
    skip?: number,
    take?: number,
  ): Observable<{ items: Item[]; total: number }> {
    const { s, t } = this.getPagination(term, skip, take);
    return this.itemService.searchItems(term, type, s, t);
  }

  //Doctors
  searchDoctors(
    term: string,
    skip?: number,
    take?: number,
  ): Observable<{ data: Doctor[]; total: number }> {
    const { s, t } = this.getPagination(term, skip, take);
    return this.doctorService.search(term, s, t);
  }

  //Companies
  searchCompanies(term: string, skip?: number, take?: number): Observable<Company[]> {
    const { s, t } = this.getPagination(term, skip, take);
    return this.companiesService.searchCompanies(term, s, t);
  }

  //Customers
  searchCustomers(
    term: string,
    type: string = 'ALL',
    skip?: number,
    take?: number,
  ): Observable<{ data: Customer[]; total: number }> {
    const { s, t } = this.getPagination(term, skip, take);
    return this.customersService.searchCustomers(term, type, s, t);
  }

  /**
   * Decide pagination dynamically based on term length
   */
  private getPagination(term: string, skip?: number, take?: number) {
    if (term.length <= 2) {
      return { s: skip ?? this.DEFAULT_SKIP, t: take ?? this.LARGE_TAKE };
    } else {
      return { s: skip ?? this.DEFAULT_SKIP, t: take ?? this.DEFAULT_TAKE };
    }
  }
}
